import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {CheckGlyph, LockGlyph} from '@/components/ui/icons'
import {KairosMark} from '@/components/kairos-mark'
import {sgsstPageGuard} from '@/components/no-access-state'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useRuta, type Etapa, type EtapaEstado} from '@/hooks/sgsst/resumen/use-ruta'
import {etapaHref} from '@/lib/ruta-nav'
import {cn} from '@/lib/utils'

function EtapaMarker({estado, index}: {estado: EtapaEstado; index: number}) {
  if (estado === 'hecha') {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
        <CheckGlyph size={13} />
      </span>
    )
  }
  if (estado === 'bloqueada') {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
        <LockGlyph />
      </span>
    )
  }
  return (
    <span
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-xs font-medium',
        estado === 'espera_usuario'
          ? 'border-tone-amber-fg/40 tone-amber'
          : 'border-primary/30 bg-accent/40 text-primary'
      )}>
      {index}
    </span>
  )
}

const ESTADO_TONE: Record<EtapaEstado, string> = {
  hecha: 'text-primary',
  en_curso: 'text-primary',
  espera_usuario: 'text-tone-amber-fg',
  bloqueada: 'text-muted-foreground',
}

function EtapaRow({etapa, index, businessId}: {etapa: Etapa; index: number; businessId: string}) {
  const {t} = useTranslation()
  const href = etapaHref(etapa.clave)
  const showCta = etapa.estado !== 'hecha' && etapa.estado !== 'bloqueada'
  return (
    // `min-w-0`: en `lg` cada etapa es una celda de la rejilla y un texto largo estiraría la columna.
    <li
      className="flex min-w-0 items-start gap-4 py-5 lg:border-b lg:border-border lg:[&:nth-child(4n)]:border-b-0"
      data-testid={`etapa-${etapa.clave}`}>
      <EtapaMarker estado={etapa.estado} index={index} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <p className="text-sm font-semibold text-foreground">{t(`ruta.etapas.${etapa.clave}`)}</p>
          <span
            className={cn('font-mono text-[10px] font-medium tracking-wider uppercase', ESTADO_TONE[etapa.estado])}
            data-testid={`etapa-${etapa.clave}-estado`}>
            {t(`ruta.estado.${etapa.estado}`)}
          </span>
        </div>
        {etapa.estado !== 'hecha' && <p className="mt-1 text-xs text-muted-foreground">{etapa.porque}</p>}
        {/* constructores-estandar-spec §3: solo el conteo que `core/ruta.py` ya trae, enlazado a la vitrina. */}
        {etapa.clave === 'mejoramiento' && etapa.datos.constructores > 0 && (
          <Link
            to={`/businesses/${businessId}/sgsst#artefactos`}
            className="mt-1.5 flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            data-testid="ruta-constructores-hint">
            {t('ruta.constructoresHint', {count: etapa.datos.constructores})}
          </Link>
        )}
      </div>
      {showCta && (
        <Button asChild size="sm" variant="outline" className="shrink-0">
          <Link to={`/businesses/${businessId}/${href}`}>{t(`ruta.cta.${etapa.clave}`)}</Link>
        </Button>
      )}
    </li>
  )
}

export function RutaPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business, isLoading: businessLoading} = useBusiness(businessId)
  const canOperate = useCapabilities(businessId).sgsst_operar
  const {data: ruta, isLoading} = useRuta(canOperate ? businessId : undefined)

  const guard = sgsstPageGuard(businessLoading || isLoading, !!business && canOperate, ruta)
  if (guard) return guard

  const puestaAlDia = ruta!.puesta_al_dia
  const titulo = puestaAlDia ? t('ruta.puestaAlDia.title', {year: ruta!.year}) : t('ruta.title')

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 text-foreground">
          <KairosMark />
          <h1 className="font-heading text-2xl" data-testid="ruta-title">
            {titulo}
          </h1>
        </div>
        {puestaAlDia && (
          <p className="mt-1 text-sm text-muted-foreground" data-testid="ruta-puesta-al-dia-lema">
            {t('ruta.puestaAlDia.subtitle')}
          </p>
        )}
      </div>

      {/* Desde `lg`, dos columnas de cuatro con flujo por columna: en zigzag habría que rastrear el número. */}
      <ol
        className="surface surface-raised divide-y divide-border px-6 py-1 lg:grid lg:grid-flow-col lg:grid-cols-[repeat(2,minmax(0,1fr))] lg:grid-rows-4 lg:gap-x-8 lg:divide-y-0"
        data-testid="ruta-etapas">
        {ruta!.etapas.map((etapa, i) => (
          <EtapaRow key={etapa.clave} etapa={etapa} index={i + 1} businessId={businessId!} />
        ))}
      </ol>
    </div>
  )
}
